import React from 'react';
import cx from 'classnames';
import moment from 'moment';
moment.updateLocale('en', { weekdaysMin: 'S_M_T_W_T_F_S'.split('_') });
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import InputAdornment from '@mui/material/InputAdornment';
import CancelIcon from '@mui/icons-material/Cancel';
import BackIcon from '@mui/icons-material/ArrowBackIos';
import ForwardIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const styles = require('./styles.pcss');

type Props = {
  onChange: Function;
  label: string;
  className: any;
  error: string;
  startDate: any;
  endDate: any;
  bottomText?: string;
  bottomTextColor?: string;
  autoCloseOnSelect?: boolean;
};

type State = {
  modalAnchor: any;
  startDate: any;
  endDate: any;
  focusedInput: any;
};

class DateRangePicker extends React.Component<Props, State> {
  containerRef: any;
  inputRef: any;

  constructor(props: any) {
    super(props);

    this.state = {
      modalAnchor: null,
      startDate: props.startDate,
      endDate: props.endDate,
      focusedInput: null,
    };

    this.containerRef = React.createRef();
    this.inputRef = React.createRef();
  }

  openModal = (event: any) => {
    this.setState({
      modalAnchor: this.containerRef,
      focusedInput: 'startDate',
    });
  };

  closeModal = () => {
    this.setState({
      modalAnchor: null,
      focusedInput: null,
    });

    this.blurInput();
  };

  blurInput = () => {
    setTimeout(() => {
      if (document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }
    }, 100);
  };

  onDatesChange = (dates: any) => {
    let [startDate, endDate] = dates || [];
    startDate = startDate ? moment(new Date(startDate)) : startDate;
    endDate = endDate ? moment(new Date(endDate)) : endDate;

    this.setState({
      startDate,
      endDate,
    });

    this.props.onChange({ startDate, endDate });

    if (this.props.autoCloseOnSelect && startDate && endDate) {
      this.closeModal();
    }
  };

  clearDates = () => {
    const resetDates = {
      startDate: null,
      endDate: null,
    }
    this.setState(resetDates);

    this.props.onChange(resetDates);
  };

  onPickerFocusChange = (focusedInput: any) => {
    let newStates = {
      focusedInput,
      modalAnchor: this.state.modalAnchor,
    };

    if (focusedInput === null) {
      newStates.modalAnchor = null;
      this.blurInput();
    }

    this.setState(newStates);
  };

  isOutsideRange = (day: any) => {
    const isBeforeDay = moment(0, 'HH').utc().isSameOrAfter(day.utc());
    let isAfterDay = false;

    if (this.state.focusedInput === 'endDate') {
      isAfterDay = day.isAfter(this.state.startDate.clone().add(28, 'days'));
    }

    return isBeforeDay || isAfterDay;
  };

  getTextValue = () => {
    let value = '';

    if (this.state.startDate !== null) {
      value += this.state.startDate.utc().format('ddd, MMM D') + ' — ';
    }

    if (this.state.endDate !== null) {
      value += this.state.endDate.utc().format('ddd, MMM D');
    }

    return value;
  };

  formatMonth = (date: any) => {
    if (!date) return '';
    return date.toLocaleString('en-US', { month: 'short' });
  };

  renderRangePicker = () => {
    return (
      <Popover
        PaperProps={{
          style: {
            boxShadow: 'none',
            border: '1px solid #C6C8CC',
            marginTop: 5,
          },
        }}
        open={Boolean(this.state.modalAnchor)}
        onClose={this.closeModal}
        anchorEl={this.state.modalAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className="singleDatePicker">
          <DatePicker
            selectsRange={true}
            startDate={
              this.state.startDate ? this.state.startDate.toDate() : undefined
            }
            endDate={
              this.state.endDate ? this.state.endDate.toDate() : undefined
            }
            onChange={(update) => {
              this.onDatesChange(update);
            }}
            open={true}
            onClickOutside={this.closeModal}
            formatWeekDay={(day) => day.substring(0, 1)}
            onBlur={this.onPickerFocusChange}
            shouldCloseOnSelect={true}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => {
              const validDate =
                date instanceof Date && !isNaN(date.getTime())
                  ? date
                  : new Date();
              const monthString = validDate
                ? `${this.formatMonth(validDate)} ${validDate.getFullYear()}`
                : '';
              return (
                <div className="custom-header">
                  <button
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    className="custom-nav-button"
                  >
                    <BackIcon />
                  </button>
                  <span>{monthString}</span>
                  <button
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    className="custom-nav-button"
                  >
                    <ForwardIcon />
                  </button>
                </div>
              );
            }}
            dateFormat="yyyy-MM-dd"
          />
        </div>
        {this.props.bottomText && (
          <div
            style={{ color: this.props.bottomTextColor }}
            className={styles.bottomText}
          >
            {this.props.bottomText}
          </div>
        )}
      </Popover>
    );
  };

  render() {
    return (
      <div
        className={cx(this.props.className, styles.dateRangePicker)}
        ref={(r: any) => (this.containerRef = r)}
      >
        <TextField
          error={Boolean(this.props.error)}
          helperText={this.props.error}
          onClick={this.openModal}
          label={this.props.label}
          variant="outlined"
          fullWidth={true}
          value={this.getTextValue()}
          className={styles.textField}
          InputProps={{
            readOnly: true,
            endAdornment: (this.state.startDate &&
              <InputAdornment position="end">
                <IconButton onClick={this.clearDates} size="large">
                  <CancelIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {this.renderRangePicker()}
      </div>
    );
  }
}

export default DateRangePicker;
